import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useMsal, useAccount } from '@azure/msal-react'
import type { AppConfig } from './config'
import { OperatorApi } from './api/client'
import { resolveOperatorRole, type OperatorRole } from './auth/roles'

export interface AppContextValue {
  config: AppConfig
  api: OperatorApi
  role: OperatorRole
  operatorName: string
}

// EXPORTED for the page-test harness (ATC-126, `src/test/renderWithApp.tsx`).
// The seam every page actually consumes is `OperatorApi` via `useApp()`, so a
// test provides one directly rather than intercepting `fetch` — nothing in the
// app calls `fetch` itself, and MSW would test a layer the pages never touch.
// Nothing in the app should import this: pages use `useApp()`.
export const AppContext = createContext<AppContextValue | null>(null)

// Provides the authenticated OperatorApi + resolved operator role to the app.
// Rendered only inside <AuthenticatedTemplate>, so an account is always present.
export function AppProvider({ config, children }: { config: AppConfig; children: ReactNode }) {
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0] ?? undefined)

  const value = useMemo<AppContextValue | null>(() => {
    if (!account) {
      return null
    }
    const api = new OperatorApi(instance, account, config)
    // The `groups` claim on the id token carries AAD group Object IDs.
    const groups = (account.idTokenClaims as { groups?: string[] } | undefined)?.groups
    const role = resolveOperatorRole(groups, config.opsGroupId)
    const operatorName = account.name ?? account.username ?? 'operator'
    return { config, api, role, operatorName }
  }, [instance, account, config])

  if (!value) {
    return null
  }
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext)
  if (!ctx) {
    throw new Error('useApp must be used within <AppProvider>')
  }
  return ctx
}
